import { useState, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { invoicesApi, paymentMethodMasterApi, agencyConfigApi, exchangeRateApi } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import type { PaymentMethod, RecordPaymentRequest, PaymentMethodListItem, ConversionPreview } from '@/types'

interface RecordPaymentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoiceId: string
  invoiceNumber: string
  balanceDue: number
  currency: string
}

const paymentMethods: { value: PaymentMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'CHECK', label: 'Check' },
  { value: 'UPI', label: 'UPI' },
  { value: 'PAYPAL', label: 'PayPal' },
  { value: 'OTHER', label: 'Other' },
]

export function RecordPaymentDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  balanceDue,
  currency,
}: RecordPaymentDialogProps) {
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState<string>(balanceDue.toString())
  const [paymentDate, setPaymentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  )
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('BANK_TRANSFER')
  const [referenceNumber, setReferenceNumber] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [paymentMethodId, setPaymentMethodId] = useState<string>('')
  const [actualAmountReceived, setActualAmountReceived] = useState<string>('')

  // Fetch payment methods from masters
  const { data: paymentMethodMasters = [] } = useQuery<PaymentMethodListItem[]>({
    queryKey: ['payment-methods'],
    queryFn: () => paymentMethodMasterApi.getAll(true),
  })

  // Fetch agency settings for base currency
  const { data: agencySettings } = useQuery({
    queryKey: ['agency-config'],
    queryFn: () => agencyConfigApi.get(),
  })

  // Check if invoice currency differs from base currency
  const showCurrencyConversion = agencySettings?.base_currency && currency !== agencySettings.base_currency

  // Get selected payment method details for forex markup
  const selectedPaymentMethodMaster = useMemo(() => {
    if (!paymentMethodId) return null
    return paymentMethodMasters.find(m => m.method_id === paymentMethodId) || null
  }, [paymentMethodId, paymentMethodMasters])

  // Fetch conversion preview when currency conversion is needed
  const conversionPreviewQuery = useQuery({
    queryKey: [
      'conversion-preview',
      amount,
      currency,
      agencySettings?.base_currency,
      selectedPaymentMethodMaster?.forex_markup_percent,
      selectedPaymentMethodMaster?.fixed_fee_amount,
      selectedPaymentMethodMaster?.fixed_fee_currency,
    ],
    queryFn: () => exchangeRateApi.getConversionPreview(
      parseFloat(amount) || 0,
      currency,
      agencySettings?.base_currency || 'USD',
      selectedPaymentMethodMaster?.forex_markup_percent,
      selectedPaymentMethodMaster?.fixed_fee_amount,
      selectedPaymentMethodMaster?.fixed_fee_currency
    ),
    enabled: !!showCurrencyConversion && !!amount && parseFloat(amount) > 0,
    staleTime: 60000, // Cache for 1 minute
  })
  const conversionPreview = conversionPreviewQuery.data as ConversionPreview | undefined
  const isLoadingPreview = conversionPreviewQuery.isLoading

  // Calculate forex gain/loss preview
  const forexGainLossPreview = useMemo(() => {
    if (!conversionPreview || !actualAmountReceived) return null
    const actual = parseFloat(actualAmountReceived)
    if (isNaN(actual) || actual <= 0) return null
    const expected = conversionPreview.converted_amount
    const gainLoss = actual - expected
    return {
      amount: gainLoss,
      isGain: gainLoss > 0,
      isLoss: gainLoss < 0,
    }
  }, [conversionPreview, actualAmountReceived])

  const recordPaymentMutation = useMutation({
    mutationFn: (data: RecordPaymentRequest) =>
      invoicesApi.recordPayment(invoiceId, data),
    onSuccess: () => {
      toast.success('Payment recorded successfully')
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] })
      onOpenChange(false)
      resetForm()
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to record payment'
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setAmount(balanceDue.toString())
    setPaymentDate(new Date().toISOString().split('T')[0])
    setPaymentMethod('BANK_TRANSFER')
    setReferenceNumber('')
    setNotes('')
    setPaymentMethodId('')
    setActualAmountReceived('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const amountValue = parseFloat(amount)
    if (isNaN(amountValue) || amountValue <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (amountValue > balanceDue) {
      toast.error(`Amount cannot exceed balance due (${formatCurrency(balanceDue, currency)})`)
      return
    }

    const data: RecordPaymentRequest = {
      amount: amountValue,
      payment_date: paymentDate,
      payment_method: paymentMethod,
      reference_number: referenceNumber || undefined,
      notes: notes || undefined,
      payment_currency: showCurrencyConversion ? currency : undefined,
      payment_method_id: paymentMethodId || undefined,
      actual_amount_received: actualAmountReceived ? parseFloat(actualAmountReceived) : undefined,
    }

    recordPaymentMutation.mutate(data)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Record a payment for invoice {invoiceNumber}. Balance due:{' '}
            <span className="font-semibold">{formatCurrency(balanceDue, currency)}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={balanceDue}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_date">Payment Date *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_method">Payment Method *</Label>
              <Select
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value as PaymentMethod)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference_number">Reference Number</Label>
              <Input
                id="reference_number"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Transaction ID, check number, etc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes about this payment..."
                rows={3}
              />
            </div>

            {/* Currency Conversion Section */}
            {showCurrencyConversion && (
              <div className="border-t pt-4 mt-4 space-y-4">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Currency Conversion
                </p>
                <p className="text-sm text-muted-foreground">
                  This invoice is in {currency} but your base currency is {agencySettings?.base_currency}.
                </p>

                {/* Conversion Preview */}
                {conversionPreview && !isLoadingPreview && (
                  <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Exchange Rate</span>
                      <span>1 {currency} = {conversionPreview.exchange_rate.toFixed(4)} {agencySettings?.base_currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Converted Amount</span>
                      <span>{formatCurrency(conversionPreview.converted_amount, agencySettings?.base_currency || 'USD')}</span>
                    </div>
                    {conversionPreview.forex_markup_amount > 0 && (
                      <div className="flex justify-between text-orange-600 dark:text-orange-400">
                        <span>Forex Markup ({conversionPreview.forex_markup}%)</span>
                        <span>+{formatCurrency(conversionPreview.forex_markup_amount, agencySettings?.base_currency || 'USD')}</span>
                      </div>
                    )}
                    {conversionPreview.fixed_fee_converted > 0 && (
                      <div className="flex justify-between text-orange-600 dark:text-orange-400">
                        <span>Fixed Fee</span>
                        <span>+{formatCurrency(conversionPreview.fixed_fee_converted, agencySettings?.base_currency || 'USD')}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium border-t pt-2">
                      <span>Estimated Total</span>
                      <span>{formatCurrency(conversionPreview.estimated_total, agencySettings?.base_currency || 'USD')}</span>
                    </div>
                  </div>
                )}
                {isLoadingPreview && (
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg text-sm text-muted-foreground">
                    Loading exchange rate...
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment_method_master">Payment Method (Master)</Label>
                    <Select
                      value={paymentMethodId || 'none'}
                      onValueChange={(value) => setPaymentMethodId(value === 'none' ? '' : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {paymentMethodMasters.map((method) => (
                          <SelectItem key={method.method_id} value={method.method_id}>
                            {method.method_name} {method.forex_markup_percent > 0 && `(+${method.forex_markup_percent}%)`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="actual_amount_received">
                      Actual Amount Received ({agencySettings?.base_currency})
                    </Label>
                    <Input
                      id="actual_amount_received"
                      type="number"
                      step="0.01"
                      min="0"
                      value={actualAmountReceived}
                      onChange={(e) => setActualAmountReceived(e.target.value)}
                      placeholder={conversionPreview ? conversionPreview.converted_amount.toFixed(2) : 'Auto-calculate'}
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the actual amount received in your bank if different from calculated
                    </p>
                  </div>
                </div>

                {/* Forex Gain/Loss Preview */}
                {forexGainLossPreview && (
                  <div className={`p-3 rounded-lg text-sm ${
                    forexGainLossPreview.isGain
                      ? 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300'
                      : forexGainLossPreview.isLoss
                        ? 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300'
                        : 'bg-gray-50 dark:bg-gray-900'
                  }`}>
                    <div className="flex justify-between font-medium">
                      <span>{forexGainLossPreview.isGain ? 'Forex Gain' : forexGainLossPreview.isLoss ? 'Forex Loss' : 'No Forex Difference'}</span>
                      <span>
                        {forexGainLossPreview.isGain ? '+' : ''}
                        {formatCurrency(forexGainLossPreview.amount, agencySettings?.base_currency || 'USD')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={recordPaymentMutation.isPending}>
              {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

